import { useCallback, useEffect, useState } from 'react';
import { SEARCH_DEBOUNCE_MS } from '../constants';
import { useDashboardUI } from '../context';
import { EMPTY_DASHBOARD_FILTERS, hasActiveDashboardFilters } from '../utils/dashboardList';

export const useDashboardFilters = () => {
  const { filters, setFilters } = useDashboardUI();
  const [localSearch, setLocalSearch] = useState(filters.search || '');
  // When `filters.search` changes to something this box did not type — the
  // empty-results "Clear filters" — the box follows it. Otherwise the debounce
  // below would put the old text straight back. Adjusted during render, the
  // way React recommends for state derived from a changing prop.
  const appliedSearch = filters.search || '';
  const [seenSearch, setSeenSearch] = useState(appliedSearch);
  if (appliedSearch !== seenSearch) {
    setSeenSearch(appliedSearch);
    if (appliedSearch !== localSearch.trim()) setLocalSearch(appliedSearch);
  }

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const normalized = localSearch.trim();
      if (normalized !== filters.search) {
        setFilters({ search: normalized });
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timeoutId);
  }, [filters.search, localSearch, setFilters]);

  const handleStateChange = useCallback(
    (value) => {
      setFilters({ state: value || '' });
    },
    [setFilters],
  );

  const handleSectionChange = useCallback(
    (value) => {
      setFilters({ sectionId: value || '' });
    },
    [setFilters],
  );

  const handleCategoryChange = useCallback(
    (value) => {
      setFilters({ categoryId: value || '' });
    },
    [setFilters],
  );

  const resetFilters = useCallback(() => {
    setLocalSearch('');
    setFilters(EMPTY_DASHBOARD_FILTERS);
  }, [setFilters]);

  const hasActiveFilters = hasActiveDashboardFilters(filters);

  return {
    localSearch,
    setLocalSearch,
    filters,
    handleStateChange,
    handleSectionChange,
    handleCategoryChange,
    resetFilters,
    hasActiveFilters,
  };
};

export default useDashboardFilters;
